import { initialize } from 'redux-form';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import {
    AccountingCountersCreateParameters,
    BillCountersCreateParameters,
    Counter,
    EnterpriseScopeCounterDiscriminator,
} from '@mews-commander/data-access';
import { lt, showSuccessAlert } from '@mews/framework';
import { createFetchAction, createUpdateValue, createBooleanUpdateValue } from '@mews-commander/utils';
import { openDialog } from '../dialog/reducer';
import {
    CREATE_COUNTER,
    CREATE_COUNTER_DIALOG,
    DELETE_COUNTER,
    EDIT_COUNTER_DIALOG,
    RESET_COUNTER,
    UPDATE_COUNTER,
} from './constants';
import { CounterFormValues } from './components/CounterForm/formMapping';

const createDialogCounterTypeMap = {
    [EnterpriseScopeCounterDiscriminator.BillCounter]: TextKey.CreateBillCounter,
    [EnterpriseScopeCounterDiscriminator.ProformaCounter]: TextKey.CreateProformaCounter,
    [EnterpriseScopeCounterDiscriminator.BillPreviewCounter]: TextKey.CreateBillPreviewCounter,
    [EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter]: TextKey.CreditNoteBillCounter,
};

export type NonSystemCounterType = keyof typeof createDialogCounterTypeMap;
export type AccountingCounterType = Exclude<
    NonSystemCounterType,
    EnterpriseScopeCounterDiscriminator.BillCounter | EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter
>;

export const openCreateCounterDialog =
    (counterType: NonSystemCounterType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(openDialog(CREATE_COUNTER_DIALOG));
        dispatch(
            initialize(CREATE_COUNTER_DIALOG, {
                titleTextKey: createDialogCounterTypeMap[counterType],
                counterType,
            })
        );
    };

export const openEditCounterDialog =
    (counterId: string, counterType: NonSystemCounterType): CommanderThunkAction =>
    (dispatch) => {
        dispatch(openDialog(EDIT_COUNTER_DIALOG));
        dispatch(
            initialize(EDIT_COUNTER_DIALOG, {
                titleTextKey: TextKey.EditBillCounter,
                counterId,
                counterType,
            })
        );
    };

export const addBillCounter =
    (values: CounterFormValues): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const billCounterCreateValues: BillCountersCreateParameters = {
            name: values.name,
            value: +values.startValue,
            isDefault: values.isDefault,
            billTypeCode: values.billTypeCode,
            format: values.numberFormat,
            title: values.title,
            displayCid: values.displayCID,
            ...(values.isDefault && { creditNoteTitle: values.creditNoteTitle }),
        };

        const promise = repositories.billCounters.add(billCounterCreateValues).then(() => {
            showSuccessAlert({ description: lt('CreateCounterSuccessMessage') });
        });

        return dispatch(createFetchAction(CREATE_COUNTER, promise));
    };

export const addAccountingCounter =
    (values: CounterFormValues, type: AccountingCounterType): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const typeToEndpointMap = {
            [EnterpriseScopeCounterDiscriminator.ProformaCounter]: repositories.proformaCounters,
            [EnterpriseScopeCounterDiscriminator.BillPreviewCounter]: repositories.billPreviewCounters,
        };

        const previewCounterCreateValues: AccountingCountersCreateParameters = {
            name: values.name,
            value: +values.startValue,
            isDefault: values.isDefault,
            format: values.numberFormat,
            title: values.title,
        };

        const promise = typeToEndpointMap[type].add(previewCounterCreateValues).then(() => {
            showSuccessAlert({ description: lt('CreateCounterSuccessMessage') });
        });

        return dispatch(createFetchAction(CREATE_COUNTER, promise));
    };

export const updateBillCounter =
    (currentCounter: Counter, values: CounterFormValues): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const billTypeCode =
            'billTypeCode' in currentCounter.data.value ? currentCounter.data.value.billTypeCode : undefined;
        const title = 'title' in currentCounter.data.value ? currentCounter.data.value.title : undefined;
        const displayCID = 'displayCid' in currentCounter.data.value ? currentCounter.data.value.displayCid : undefined;
        const creditNoteTitle =
            'creditNoteTitle' in currentCounter.data.value ? currentCounter.data.value.creditNoteTitle : undefined;
        const newCreditNoteTitle = values.isDefault ? values.creditNoteTitle : undefined;

        const billCounterUpdateValues = {
            name: createUpdateValue(currentCounter.name, values.name),
            isDefault: createBooleanUpdateValue(currentCounter.isDefault, values.isDefault),
            format: createUpdateValue(currentCounter.format, values.numberFormat),
            billTypeCode: createUpdateValue(billTypeCode, values.billTypeCode),
            title: createUpdateValue(title, values.title),
            counterId: currentCounter.id,
            displayCid: createBooleanUpdateValue(displayCID, values.displayCID),
            creditNoteTitle: createUpdateValue(creditNoteTitle, newCreditNoteTitle),
        } as const;

        const promise = repositories.billCounters.update(billCounterUpdateValues).then(() => {
            showSuccessAlert({ description: lt('UpdateCounterSuccessMessage') });
        });

        return dispatch(createFetchAction(UPDATE_COUNTER, promise));
    };

export const updateAccountingCounter =
    (currentCounter: Counter, values: CounterFormValues): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const title = 'title' in currentCounter.data.value ? currentCounter.data.value.title : undefined;

        const accountingCounterUpdateValues = {
            name: createUpdateValue(currentCounter.name, values.name),
            isDefault: createBooleanUpdateValue(currentCounter.isDefault, values.isDefault),
            format: createUpdateValue(currentCounter.format, values.numberFormat),
            title: createUpdateValue(title, values.title),
            counterId: currentCounter.id,
        } as const;

        const typeToEndpointMap = {
            [EnterpriseScopeCounterDiscriminator.ProformaCounter]: repositories.proformaCounters,
            [EnterpriseScopeCounterDiscriminator.BillPreviewCounter]: repositories.billPreviewCounters,
            [EnterpriseScopeCounterDiscriminator.AvailabilityBlockCounter]: repositories.availabilityBlockCounters,
            [EnterpriseScopeCounterDiscriminator.ServiceOrderCounter]: repositories.serviceOrderCounters,
            [EnterpriseScopeCounterDiscriminator.RegistrationCardCounter]: repositories.registrationCardCounters,
            [EnterpriseScopeCounterDiscriminator.CorrectionBillCounter]: repositories.correctionBillCounters,
            [EnterpriseScopeCounterDiscriminator.PaymentConfirmationBillCounter]:
                repositories.paymentConfirmationBillCounters,
        } as const;

        const promise = typeToEndpointMap[currentCounter.data.discriminator as keyof typeof typeToEndpointMap]
            .update(accountingCounterUpdateValues)
            .then(() => {
                showSuccessAlert({ description: lt('UpdateCounterSuccessMessage') });
            });

        return dispatch(createFetchAction(UPDATE_COUNTER, promise));
    };

export const deleteCounter =
    (counterId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const promise = repositories.counters.delete({ counterId }).then(() => {
            showSuccessAlert({ description: lt('DeleteCounterSuccessMessage') });
        });
        return dispatch(createFetchAction(DELETE_COUNTER, promise));
    };

export const resetCounter =
    (counterId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const promise = repositories.counters.reset({ counterId }).then(() => {
            showSuccessAlert({ description: lt('ResetCounterSuccessMessage') });
        });
        return dispatch(createFetchAction(RESET_COUNTER, promise));
    };
